import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, CalendarDays, RefreshCw, CheckCircle2, AlertCircle, Clock, X, Zap } from 'lucide-react';
import { SocialIcon } from './SocialIcon';

interface ScheduledPost {
  _id: string;
  contentId: string;
  platform: string;
  scheduledAt: string;
  status: 'scheduled' | 'posted' | 'failed';
  content: { contentText: string; hook?: string; cta?: string };
  article?: { title: string };
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50";

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function PostingCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<ScheduledPost | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [nextRunIn, setNextRunIn] = useState<number | null>(null);

  useEffect(() => {
    const fetchAutomationStatus = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await fetch('/api/admin/jobs/automation-status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const json = await res.json();
          setNextRunIn(json.automation?.nextRunIn ?? null);
        }
      } catch { /* non-critical */ }
    };
    fetchAutomationStatus();
  }, []);

  useEffect(() => { fetchPosts(); }, [currentDate]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const params = new URLSearchParams({
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString(),
      });
      const response = await fetch(`/api/scheduler/posts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load posting schedule');
    } finally {
      setLoading(false);
    }
  };

  const getPostsForDate = (day: number) => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), day + 1);
    return posts.filter(p => { const d = new Date(p.scheduledAt); return d >= start && d < end; });
  };

  const getStatusIcon = (status: string) => {
    if (status === 'posted') return <CheckCircle2 size={11} className="text-green" />;
    if (status === 'failed') return <AlertCircle size={11} className="text-red" />;
    return <Clock size={11} className="text-orange" />;
  };

  const getStatusCls = (status: string) => {
    if (status === 'posted') return 'bg-green/15 text-green';
    if (status === 'failed') return 'bg-red/15 text-red';
    if (status === 'scheduled') return 'bg-orange/15 text-orange';
    return 'bg-primary/15 text-primary';
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toDateString();

  const upcomingPosts = posts
    .filter(p => new Date(p.scheduledAt) >= new Date())
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
    .slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Posting Calendar</h1>
          <div className="flex items-center gap-1.5 mt-1">
            <div className="relative">
              <div className="w-1.5 h-1.5 rounded-full bg-green" />
              <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-green animate-ping opacity-60" />
            </div>
            <span className="text-white/40 text-xs">
              Auto-posting active
              {nextRunIn !== null && ` · next run in ${nextRunIn} min`}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex items-center gap-1.5 bg-green/10 border border-green/20 text-green rounded-lg px-3 py-2 text-xs font-semibold">
            <Zap size={12} /> Posts every 5 min
          </div>
          <button onClick={() => setCurrentDate(new Date())} className={BTN_SECONDARY}>
            <CalendarDays size={14} /> Today
          </button>
          <button onClick={fetchPosts} disabled={loading} className={BTN_SECONDARY}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}

      {/* Calendar */}
      <div className={`${CARD} p-5`}>
        {/* Month Nav */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white">{MONTH_NAMES[month]} {year}</h2>
          <div className="flex gap-1">
            <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
              className="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/60 flex items-center justify-center transition-colors">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
              className="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white/60 flex items-center justify-center transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-white/30 uppercase tracking-wider">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDayOfWeek }, (_, i) => (
            <div key={`e-${i}`} className="h-20 rounded-lg bg-white/[0.01] border border-white/[0.03]" />
          ))}

          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dayPosts = getPostsForDate(day);
            const isToday = new Date(year, month, day).toDateString() === todayStr;

            return (
              <div
                key={day}
                onClick={() => { if (dayPosts.length > 0) { setSelectedPost(dayPosts[0]); setDialogOpen(true); } }}
                className={`h-20 rounded-lg border p-1.5 transition-colors relative
                  ${isToday ? 'border-primary/40 bg-primary/[0.06]' : 'border-white/[0.05] bg-white/[0.02]'}
                  ${dayPosts.length > 0 ? 'cursor-pointer hover:bg-white/[0.05]' : ''}`}
              >
                <span className={`text-xs font-semibold ${isToday ? 'text-primary' : 'text-white/50'}`}>{day}</span>
                {dayPosts.length > 0 && (
                  <div className="absolute bottom-1.5 left-1 right-1 space-y-0.5">
                    {dayPosts.slice(0, 2).map(p => (
                      <div key={p._id} className="flex items-center gap-0.5">
                        <SocialIcon platform={p.platform} size={10} />
                        {getStatusIcon(p.status)}
                      </div>
                    ))}
                    {dayPosts.length > 2 && (
                      <p className="text-[9px] text-white/30">+{dayPosts.length - 2} more</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Posts Table */}
      <div className={CARD}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-white">Upcoming Posts This Month</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
          </div>
        ) : upcomingPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-white/40 text-sm">No upcoming posts scheduled for this month</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Date', 'Platform', 'Article', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {upcomingPosts.map((post) => (
                  <tr key={post._id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-white/70 text-xs">{new Date(post.scheduledAt).toLocaleDateString()}</p>
                      <p className="text-white/30 text-xs">{new Date(post.scheduledAt).toLocaleTimeString()}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <SocialIcon platform={post.platform} size={16} />
                        <span className="text-white/60 text-xs capitalize">{post.platform}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/60 text-xs max-w-[200px] truncate">
                      {post.article?.title || 'Unknown Article'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {getStatusIcon(post.status)}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusCls(post.status)}`}>
                          {post.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setSelectedPost(post); setDialogOpen(true); }}
                        className="px-2.5 py-1 text-xs rounded-lg bg-primary/15 text-primary hover:bg-primary/25 transition-colors">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Post Detail Dialog */}
      <Dialog.Root open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111113] border border-white/[0.08] rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-base font-semibold text-white flex items-center gap-2">
                <SocialIcon platform={selectedPost?.platform || ''} size={20} />
                Post Details — <span className="capitalize">{selectedPost?.platform}</span>
              </Dialog.Title>
              <Dialog.Close className="text-white/40 hover:text-white transition-colors"><X size={18} /></Dialog.Close>
            </div>

            {selectedPost && (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-white/40 mb-1">Article</p>
                  <p className="text-white/80 text-sm font-medium">{selectedPost.article?.title}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-white/50 text-xs">
                    Scheduled: {new Date(selectedPost.scheduledAt).toLocaleString()}
                  </p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusCls(selectedPost.status)}`}>
                    {selectedPost.status}
                  </span>
                </div>

                <div>
                  <p className="text-xs font-semibold text-white/40 mb-2">Content</p>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-4 text-white/70 text-sm whitespace-pre-wrap">
                    {selectedPost.content.contentText}
                  </div>
                </div>

                {selectedPost.content.hook && (
                  <div>
                    <p className="text-xs font-semibold text-primary/80 mb-1">Hook</p>
                    <div className="bg-primary/[0.06] border border-primary/20 rounded-lg p-3 text-white/70 text-sm">
                      {selectedPost.content.hook}
                    </div>
                  </div>
                )}

                {selectedPost.content.cta && (
                  <div>
                    <p className="text-xs font-semibold text-green/80 mb-1">Call to Action</p>
                    <div className="bg-green/[0.06] border border-green/20 rounded-lg p-3 text-white/70 text-sm">
                      {selectedPost.content.cta}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end mt-6 pt-4 border-t border-white/[0.06]">
              <button onClick={() => setDialogOpen(false)}
                className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors">
                Close
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default PostingCalendar;
